import Papa from 'papaparse';
import * as React from 'react';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ProjectResourceType } from 'src/constants/project';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { addProjectResourceToDiagramModel } from 'src/containers/ProjectDiagram/utils/addProjectResourceToDiagramModel';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { transformModelToSnapshot } from 'src/containers/ProjectDiagram/utils/transformModelToSnapshot';
import { useCreateMappings } from 'src/containers/ProjectResourceSuggestionsDialog/hooks/useCreateMappings';
import { getMappingSuggestions } from 'src/containers/ProjectResourceSuggestionsDialog/utils/getMappingSuggestions';
import { projectSelector } from 'src/selectors/project';
import {
  ProjectFileUpload,
  ProjectFileUploadField,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import { generateDateRandomId } from 'src/utils/generateId';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useGenerateData } from 'src/utils/voicebox/hooks/useGenerateData';
import { transformGenesisMappingsFromDiagram } from 'src/utils/voicebox/transformGenesisMappingsFromDiagram';

/**
 * Generate data for existing model (no resources, with classes)
 * - Generate synthetic CSV data for selected classes
 * - Import CSV files
 * - Extract model JSON-LD from diagram
 * - Transform and apply mappings
 */
export const useGenerateMockData = () => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const projectFromRedux = useReduxSelector(projectSelector(projectId));

  const generateData = useGenerateData();
  const handleCreateMappings = useCreateMappings();
  const isMounted = useIsMounted();

  return React.useCallback(
    async (
      selectedClasses: string[]
    ): Promise<{
      success: boolean;
      diagram?: ExtendedDiagram;
    }> => {
      if (!projectFromRedux || !projectId || !diagram) {
        return { success: false };
      }

      // Use the diagram from the hook directly instead of reading from Redux store
      // This ensures we have the latest state after any diagram mutations
      const projectDiagramSnapshot = transformModelToSnapshot(diagram.model);
      const project = {
        ...projectFromRedux,
        diagram: {
          ...projectFromRedux.diagram,
          model: projectDiagramSnapshot,
        },
      };

      const dataResult = await generateData({
        project,
        classIris: selectedClasses,
      });
      if (!isMounted.current) {
        return { success: false };
      }
      if (!dataResult) {
        return { success: false };
      }

      const filesToUpload: Array<Omit<ProjectFileUpload, 'dateLastUpdated'>> =
        [];
      const tableIdToFileId: Record<string, string> = {};

      dataResult.data.forEach((csvData) => {
        const parseResult = Papa.parse(csvData.data, {
          header: true,
          skipEmptyLines: true,
        });

        const fields: ProjectFileUploadField[] = parseResult.meta.fields
          ? parseResult.meta.fields.map((field) => ({
              value: field,
              label: field,
              statistics: { distinct: 100, records: parseResult.data.length },
            }))
          : [];

        const fileId = generateDateRandomId();

        filesToUpload.push({
          name: csvData.name,
          path: csvData.name,
          contents: csvData.data,
          options: defaultProjectFileUploadOptions,
          fields,
          id: fileId,
          partial: false,
          isGeneratedMockData: true,
        });

        const mapping = dataResult.mappings.find(
          (m) => m.table_name === csvData.name
        );
        if (mapping) {
          tableIdToFileId[mapping.table_id] = fileId;
        }
      });

      const dateLastUpdated = Date.now();
      const projectFileUploads: ProjectFileUpload[] = filesToUpload.map(
        (file) => ({
          ...file,
          dateLastUpdated,
        })
      );

      commitDiagramModel(
        diagram,
        (model, d) => {
          projectFileUploads.forEach((projectFileUpload) => {
            addProjectResourceToDiagramModel(
              projectFileUpload,
              ProjectResourceType.FILE_UPLOAD
            )(model, d);
          });
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );

      dispatch(
        projectsActionCreators.addedFileUploadsToProject(
          projectId,
          filesToUpload
        )
      );

      diagram.undoManager.clear();

      const resourceById: Record<string, ProjectFileUpload> = {};
      dataResult.mappings.forEach((mapping) => {
        const fileId = tableIdToFileId[mapping.table_id];
        if (fileId) {
          const file = filesToUpload.find((f) => f.id === fileId);
          if (file) {
            resourceById[mapping.table_id] = {
              ...file,
              dateLastUpdated: Date.now(),
            };
          }
        }
      });

      const diagramSnapshot = transformModelToSnapshot(diagram.model);

      const suggestionsWithoutResource = transformGenesisMappingsFromDiagram(
        dataResult.mappings,
        diagramSnapshot
      );

      const suggestions = suggestionsWithoutResource
        .map((suggestion) => {
          const resource = suggestion.source
            ? resourceById[suggestion.source]
            : null;
          return resource ? { ...suggestion, resource } : null;
        })
        .filter((s): s is NonNullable<typeof s> => s !== null);

      const { mappingSuggestions } = getMappingSuggestions({
        labelsByIri: {},
        mappings: [],
        selectedClasses,
        suggestions,
      });

      if (mappingSuggestions.length) {
        await handleCreateMappings(mappingSuggestions, true, true);
      }

      return { success: true, diagram };
    },
    [
      projectFromRedux,
      projectId,
      diagram,
      generateData,
      dispatch,
      handleCreateMappings,
      isMounted,
    ]
  );
};
